import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Divider,
  CircularProgress,
  Link
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

const steps = ['Email de Recuperação', 'Verificação de Código', 'Nova Senha'];

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);

  const checkPasswordStrength = (password: string) => {
    if (!password) return null;
    if (password.length < 8) return 'weak';
    if (
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password)
    ) {
      return 'strong';
    }
    return 'medium';
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPwd = e.target.value;
    setNewPassword(newPwd);
    setPasswordStrength(checkPasswordStrength(newPwd));
  };

  const handleSendResetEmail = async () => {
    setError('');
    setSuccess('');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Por favor, insira um email válido.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setSuccess('Se o email estiver cadastrado, você receberá um código de recuperação.');
      setActiveStep(1);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao enviar email. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setSuccess('');

    if (!verificationCode || verificationCode.length !== 6 || !/^\d+$/.test(verificationCode)) {
      setError('Por favor, insira um código válido de 6 dígitos.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/verify-reset-code`, { email, code: verificationCode });
      setActiveStep(2);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Código inválido ou expirado.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError('');
    setSuccess('');

    if (!newPassword || newPassword.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        email,
        code: verificationCode,
        new_password: newPassword,
      });
      setActiveStep(3);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao redefinir senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <MailOutlineIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Informe seu email de cadastro</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Enviaremos um código de verificação para seu email cadastrado.
            </Typography>
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              type="email"
              required
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleSendResetEmail()}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleSendResetEmail}
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Enviar código'}
            </Button>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <VpnKeyIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Insira o código de verificação</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Digite o código de 6 dígitos que enviamos para <strong>{email}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Código de verificação"
              variant="outlined"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              margin="normal"
              required
              autoFocus
              inputProps={{ maxLength: 6, style: { letterSpacing: 8, textAlign: 'center', fontSize: 24, fontWeight: 700 } }}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyCode}
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Verificar código'}
            </Button>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Não recebeu o código?{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    setActiveStep(0);
                    setVerificationCode('');
                    setError('');
                  }}
                >
                  Enviar novamente
                </Link>
              </Typography>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LockResetIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Defina sua nova senha</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Crie uma senha segura com pelo menos 8 caracteres, incluindo letras maiusculas, minusculas e numeros.
            </Typography>
            <TextField
              fullWidth
              label="Nova senha"
              variant="outlined"
              value={newPassword}
              onChange={handlePasswordChange}
              margin="normal"
              type="password"
              required
              autoFocus
            />
            {passwordStrength && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Forca da senha:
                </Typography>
                <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'grey.200', overflow: 'hidden' }}>
                  <Box sx={{
                    height: '100%',
                    width: passwordStrength === 'weak' ? '30%' : passwordStrength === 'medium' ? '60%' : '100%',
                    bgcolor: passwordStrength === 'weak' ? 'error.main' : passwordStrength === 'medium' ? 'warning.main' : 'success.main',
                  }} />
                </Box>
                <Typography variant="caption" color={
                  passwordStrength === 'weak' ? 'error' : passwordStrength === 'medium' ? 'warning.main' : 'success.main'
                }>
                  {passwordStrength === 'weak' ? 'Fraca' : passwordStrength === 'medium' ? 'Media' : 'Forte'}
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="Confirme a nova senha"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              type="password"
              required
              onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleResetPassword}
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Redefinir senha'}
            </Button>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Senha redefinida com sucesso!</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Sua senha foi alterada. Agora voce pode entrar em sua conta com a nova senha.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>
              Ir para o login
            </Button>
          </Box>
        );
      default:
        return 'Passo desconhecido';
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button startIcon={<ArrowBackIcon />} component={RouterLink} to="/login" sx={{ mr: 2 }}>
            Voltar
          </Button>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Recuperação de Senha
          </Typography>
        </Box>

        {activeStep < 3 && (
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        )}

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        {getStepContent(activeStep)}

        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" color="text.secondary" align="center">
          Se precisar de ajuda, entre em contato com nosso{' '}
          <Link component={RouterLink} to="/help">suporte</Link>
        </Typography>
      </Paper>
    </Container>
  );
};

export default ResetPasswordPage;
